<script setup lang="ts">
// @ts-ignore
// eslint-disable-next-line camelcase
import { atcb_action } from 'add-to-calendar-button';
import dayjs from 'dayjs';
import { Head } from '@vueuse/head';

import defaultLogoImageUrl from '@/assets/svg/peatix-logo-gray.svg';
import defaultMobileLogoImageUrl from '@/assets/svg/peatix-logo-gray-mobile.svg';
import ChevronDownIcon from '@/components/icons/ChevronDownIcon.vue';
import ChevronUpIcon from '@/components/icons/ChevronUpIcon.vue';
import type { EventDetail, EventTicket } from '@/types/event';
import type {
	CartOrderForm,
	CheckoutsOrdersCartItemRequest,
	CheckoutsOrdersRequest,
	SurveyFormItemRequest,
	SurveyFormRequest,
} from '@/types/checkout';
import type { TICKET_SELECTION_MODE as TICKET_SELECTION_MODE_ENUM } from '@/enums/checkout';

enum AFTER_AUTHENTICATION_STEP {
	FOLLOW = 'follow',
	MESSAGE = 'message',
	CHECKOUT = 'checkout',
}

const props = defineProps({
	event: {
		type: Object as PropType<EventDetail | null>,
		required: true,
	},
	isPreviewMode: {
		type: Boolean,
		required: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
});

const { trackEvent } = useGtm();
const { isMobile } = useWindowResize();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const profileStore = useProfileStore();
const groupStore = useGroupStore();
const eventStore = useEventStore();
const checkoutStore = useCheckoutStore();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const attendeeStore = useAttendeeStore();
const {
	isTranslating,
	translate,
	isTranslatedView,
	setTranslatedView,
	trackTranslationSuccess,
} = useTranslation();

const afterAuthenticationStep = ref();
const btnAddGoogleCalendar = ref();
const showMap = ref(false);
const showContactDialog = ref(false);
const showSendMessageDialog = ref(false);
const showAddCalendarDialog = ref(false);
const isOpenCheckoutSidebar = ref(false);
const isOpenAuthenticationSidebar = ref(false);
const isOpenQuestionnaireFormSideBar = ref(false);
const isOpenTicketPaymentFormSideBar = ref(false);
const isOpenCheckoutProcessStatusSideBar = ref(false);
const isOpenTokenGateProcessingSidebar = ref(false);
const isShowGoogleLoginFailDialog = ref(false);
const ticketSelectionMode = ref(TICKET_SELECTION_MODE.TICKETS);
const checkoutProcessStatusTemplate = ref();
const paymentMethod = ref();
const followBtnRef = ref();
const routeOrderId = ref('');
const isPaidToken = ref(false);
const routeTokenId = ref('');

const originalText = reactive<{
	title: string
	description: string
	groupName: string
	tickets: EventTicket[]
}>({
	title: '',
	description: '',
	groupName: '',
	tickets: [],
});
const translatedText = reactive<{
	title: string
	description: string
	groupName: string
	tickets: EventTicket[]
}>({
	title: '',
	description: '',
	groupName: '',
	tickets: [],
});

const eventId = computed(() => {
	return props.event?.id || null;
});

const coverImageUrl = computed(() => {
	const defaultLogo = isMobile.value ? defaultMobileLogoImageUrl : defaultLogoImageUrl;
	return props.event?.coverUrl ? props.event?.coverUrl : defaultLogo;
});

const toggleMapText = computed(() => {
	return showMap.value ? 'Hide map' : 'Show map';
});

const displayDateTime = computed(() => convertToDisplayStartAndEndDateTime(
	props.event?.startDateTime || '',
	props.event?.endDateTime || '',
	props.event?.timezone || '',
));

const tickets = computed(() => {
	return props.event?.tickets;
});

const hasTokenGatedTicket = computed(() => {
	return tickets.value?.some((ticket) => ticket.requiresTokenGates);
});

const hasOnlyTokenGatedTicketInEvent = computed(() => {
	const hasOnlyOneTicket = tickets.value?.length === 1;
	const firstTicketIsTokenGated = tickets.value?.[0].requiresTokenGates;
	return hasOnlyOneTicket && firstTicketIsTokenGated;
});

const isAllTicketSoldOut = computed(() => {
	return props.event?.tickets.every((ticket) => ticket.status === EVENT_TICKET_STATUS.SOLD_OUT ||
      ticket.status === EVENT_TICKET_STATUS.SUSPENDED);
});

const isPublished = computed(() => {
	return props.event?.status === EVENT_STATUS.OPEN;
});

const isDraft = computed(() => {
	return props.event?.status === EVENT_STATUS.DRAFT;
});

const isShowAction = computed(() => {
	if (props.event?.externalTicketingUrl) {
		return isPublished.value || isDraft.value;
	}
	return (isPublished.value || isDraft.value) && !isAllTicketSoldOut.value;
});

const chevronIconComponent = computed(() => (showMap.value ? ChevronUpIcon : ChevronDownIcon));
const formId = computed(() => checkoutStore.surveyFormId);
const orderSalesId = computed(() => checkoutStore.salesId);
const orderStatus = computed(() => checkoutStore.checkoutOrderStatusResponse.orderStatus);
const isAttend = computed(() => userStore.hasTicketOwnership);
const profileId = computed(() => Number(profileStore.profileId));
const organizerGroupId = computed(() => props.event?.organizerGroupId);
const checkoutOrderId = computed(() => checkoutStore.tempOrderId);
const isUSEvent = computed(() => eventStore.isUSEvent);
const isupportedCountryEvent = computed(() => eventStore.isPaymentSupportedCountryEvent);
const eventCountryId = computed(() => eventStore.eventDetail?.countryId || null);

const orderFormId = computed(() => {
	if (orderSalesId.value) {
		return attendeeStore.orderData?.surveyFormsId;
	}
	// If an order return failed status, get a form ID from internal state instead
	return formId.value;
});

const orderPromoCodeId = computed(() => {
	if (orderSalesId.value) {
		return attendeeStore.orderData?.promotion?.id;
	}
	// If an order return failed status, get a promo code from internal state instead
	return checkoutStore.cart.promoCode;
});

const orderPromoCode = computed(() => {
	if (orderSalesId.value) {
		return attendeeStore.orderData?.promotion?.code;
	}
	// If an order return failed status, get a promo code from internal state instead
	return checkoutStore.cart.promoCode;
});

const orderTicketIds = computed(() => {
	if (orderSalesId.value) {
		return attendeeStore.orderTicketIds;
	}
	// If an order return failed status, get ticket ids from internal state instead
	return checkoutStore.orderTicketIds;
});

const orderTotalAmount = computed(() => {
	if (orderSalesId.value) {
		return attendeeStore.orderData?.amountPaid;
	}
	// If an order return failed status, get a total amount from internal state instead
	return checkoutStore.checkoutOrdersData?.totalAmount;
});

const organizerName = computed(() => {
	return `Organizer: ${userStore.user?.name}`;
});

const isOwner = computed(() => {
	return props.event?.organizerId === profileStore.profileId;
});

const isDisabledPurchaseButton = computed(() => {
	if (props.event?.externalTicketingUrl) {
		return false;
	}
	// Only allow open status event to be able to purchase tickets
	return props.event?.isSoldout && isPublished.value;
});

const isShowAlert = computed(() => tickets.value?.length && !props.isLoading && !isupportedCountryEvent.value);

const userRole = computed(() => {
	if (userStore.role.isNotMember) {
		return null;
	}
	return userStore.userRole;
});

const displayText = computed(() => {
	return isTranslatedView.value ? translatedText : originalText;
});

const sidebarTickets = computed(() => {
	if (!isTranslatedView.value) {
		return checkoutStore.ticketsInventoryList;
	}
	// Map translated names onto the inventory list
	const nameMap = new Map(translatedText.tickets.map((t) => [t.id, t.name]));
	return checkoutStore.ticketsInventoryList.map((ticket) => ({
		...ticket,
		name: nameMap.get(ticket.id) || ticket.name,
	}));
});

const isTranslationDisabled = computed(() => {
	return isTranslating.value || !props.event;
});

const translationButtonText = computed(() => {
	if (!props.event) {
		return 'Loading...';
	}
	return isTranslatedView.value ? 'Undo translation' : 'Translate this page';
});

function handleAddToGoogleCalendar(calendarType: string) {
	if (!props.event) {
		return;
	}
	// https://add-to-calendar-button.com/advanced-use#case-10
	const startDate = convertDateTimeFromUtcToTimezone(
		props.event.startDateTime,
		props.event.timezone,
		AMERICAN_DATE_FORMAT,
	);
	const startTime = convertDateTimeFromUtcToTimezone(
		props.event.startDateTime,
		props.event.timezone,
		TIME_24_HOUR_FORMAT,
	);
	const endDate = convertDateTimeFromUtcToTimezone(
		props.event.endDateTime,
		props.event.timezone,
		AMERICAN_DATE_FORMAT,
	);
	const endTime = convertDateTimeFromUtcToTimezone(
		props.event.endDateTime,
		props.event.timezone,
		TIME_24_HOUR_FORMAT,
	);
	const config = {
		name: displayText.value.title,
		description: displayText.value.description,
		startDate,
		startTime,
		endDate,
		endTime,
		options: [calendarType],
		timeZone: props.event.timezone,
		iCalFileName: displayText.value.title,
		...(props.event.locationType === EVENT_TYPE.PHYSICAL && {
			location: props.event.location?.name,
		}),
	};
	atcb_action(config, btnAddGoogleCalendar.value);
	showAddCalendarDialog.value = false;
}

function getUserInfo(id: number) {
	userStore.getUserById(id);
}

function generateCreateCheckoutOrderPayload(): Partial<CheckoutsOrdersRequest> | null {
	if (!eventId.value) {
		return null;
	}
	const orders = checkoutStore.cart.orders.reduce(
		(result: CheckoutsOrdersCartItemRequest[], cartItem: CartOrderForm) => {
			if (cartItem.quantity > 0) {
				result.push({
					itemId: cartItem.id,
					quantity: cartItem.quantity,
				});
			}
			return result;
		},
		[],
	);

	const callbackUrl = getFullUrlPath(route.path);
	return {
		eventId: eventId.value,
		orders: {
			type: 'tickets',
			cart: orders,
		},
		promocode: checkoutStore.cart.promoCode,
		callbackUrl,
	};
}

function generateSurveyFormData(): SurveyFormRequest | null {
	const hasSurveyForm = !!Object.keys(checkoutStore.surveyFormData).length;
	if (!hasSurveyForm || !formId.value) {
		return null;
	}

	const surveyFormItems = Object.entries(checkoutStore.surveyFormData).map(([fieldId, value]): SurveyFormItemRequest => {
		let fieldValue = value;
		if (dayjs(fieldValue, DATE_TIME_ISO8601_FORMAT).isValid()) {
			const timezone = profileStore.profile?.timezone || DEFAULT_TIMEZONE;
			fieldValue = convertDateTimeToUtc(fieldValue, timezone, AMERICAN_DATE_FORMAT);
		}
		return {
			fieldId: Number(fieldId),
			fieldValue,
		};
	});

	return {
		formId: formId.value,
		data: surveyFormItems,
	};
}

async function createCartOrder() {
	// Create checkout order
	const checkoutOrderPayload = generateCreateCheckoutOrderPayload();
	if (checkoutOrderPayload) {
		await checkoutStore.createCheckoutOrder(checkoutOrderPayload);
	}
}

function handleStartCheckout(ticketType: TICKET_SELECTION_MODE_ENUM) {
	if (props.isPreviewMode) {
		return;
	}

	if (props.event?.externalTicketingUrl) {
		let externalTicketingUrl = props.event?.externalTicketingUrl;
		// Check if the URL starts with "http://" or "https://"
		if (!/^https?:\/\//i.test(props.event?.externalTicketingUrl)) {
			externalTicketingUrl = `https://${props.event?.externalTicketingUrl}`;
		}
		openLink(externalTicketingUrl, '_blank');
		return;
	}

	if (authStore.isLoggedIn) {
		ticketSelectionMode.value = ticketType;
		isOpenCheckoutSidebar.value = true;
	} else {
		isOpenAuthenticationSidebar.value = true;
	}
}

function handleSidebarClose() {
	// TODO: Check discard cart
	// Reset cart state
	checkoutStore.cart.orders = [];
	checkoutStore.cart.promoCode = '';
	checkoutStore.checkoutOrderNewResponse.data = null;
	checkoutStore.ticketsInventoryResponse.data = null;
	checkoutStore.ticketPromotionsResponse.promotionErrorMessage = '';
	checkoutStore.ticketPromotionsResponse.data = null;
	checkoutStore.surveyFormData = {};

	// Reset side bar state
	isOpenCheckoutSidebar.value = false;
	isOpenAuthenticationSidebar.value = false;
	isOpenTicketPaymentFormSideBar.value = false;
	isOpenCheckoutProcessStatusSideBar.value = false;
	isOpenQuestionnaireFormSideBar.value = false;
}

async function handlePlaceTokenGateOrder() {
	if (!checkoutOrderId.value) {
		return;
	}

	// Get redirect url
	await checkoutStore.getCheckoutOrdersStatusById(checkoutOrderId.value);

	// Redirect to token gate url
	if (checkoutStore.paymentUrl) {
		openLinkInCurrentTab(checkoutStore.paymentUrl);
	}
}

async function handleCartAdded() {
	if (!authStore.isLoggedIn) {
		// Check if user logged in
		isOpenCheckoutSidebar.value = false;
		isOpenAuthenticationSidebar.value = true;
		afterAuthenticationStep.value = AFTER_AUTHENTICATION_STEP.CHECKOUT;
	} else if (formId.value) {
		// Check if event has questionnaire.
		isOpenCheckoutSidebar.value = false;
		isOpenQuestionnaireFormSideBar.value = true;
	} else {
		await createCartOrder();
		if (checkoutStore.checkoutOrderNewResponse.errorMessage) {
			notificationStore.showErrorNotification(checkoutStore.checkoutOrderNewResponse.errorMessage);
			return;
		}

		switch (ticketSelectionMode.value) {
			case TICKET_SELECTION_MODE.TICKETS:
				isOpenTicketPaymentFormSideBar.value = true;
				isOpenCheckoutSidebar.value = false;
				break;
			case TICKET_SELECTION_MODE.TOKEN_GATE:
				await handlePlaceTokenGateOrder();
				break;
			default:
				break;
		}
	}
}

async function handleSubmittedSurveyForm() {
	// Create checkout order
	await createCartOrder();
	if (checkoutStore.checkoutOrderNewResponse.errorMessage) {
		notificationStore.showErrorNotification(checkoutStore.checkoutOrderNewResponse.errorMessage);
		return;
	}

	// Patch update survey form
	if (!checkoutOrderId.value) {
		return;
	}

	const form = generateSurveyFormData();
	if (!form) {
		return;
	}
	await checkoutStore.updateSurveyFormOrderId(checkoutOrderId.value, form);
	if (checkoutStore.surveyFormResponse.errorMessage) {
		notificationStore.showErrorNotification(checkoutStore.surveyFormResponse.errorMessage);
	}

	switch (ticketSelectionMode.value) {
		case TICKET_SELECTION_MODE.TICKETS:
			isOpenTicketPaymentFormSideBar.value = true;
			isOpenQuestionnaireFormSideBar.value = false;
			break;
		case TICKET_SELECTION_MODE.TOKEN_GATE:
			await handlePlaceTokenGateOrder();
			break;
		default:
			break;
	}
}

function handleBackFromQuestionnaire() {
	isOpenQuestionnaireFormSideBar.value = false;
	isOpenCheckoutSidebar.value = true;
}

function handleBackFromPayment() {
	isOpenTicketPaymentFormSideBar.value = false;
	if (formId.value) {
		isOpenQuestionnaireFormSideBar.value = true;
	} else {
		isOpenCheckoutSidebar.value = true;
	}
}

async function handleContinueCheckoutFromLogin(isLoggedIn: boolean) {
	if (!isLoggedIn) {
		return;
	}

	// Check if event has questionnaire.
	if (formId.value) {
		isOpenAuthenticationSidebar.value = false;
		isOpenQuestionnaireFormSideBar.value = true;
	} else {
		// Back to checkout sidebar before create order
		isOpenAuthenticationSidebar.value = false;
		isOpenCheckoutSidebar.value = true;

		// Create order
		await createCartOrder();
		if (checkoutStore.checkoutOrderNewResponse.errorMessage) {
			notificationStore.showErrorNotification(checkoutStore.checkoutOrderNewResponse.errorMessage);
			return;
		}

		switch (ticketSelectionMode.value) {
			case TICKET_SELECTION_MODE.TICKETS:
				isOpenTicketPaymentFormSideBar.value = true;
				isOpenCheckoutSidebar.value = false;
				isOpenAuthenticationSidebar.value = false;
				break;
			case TICKET_SELECTION_MODE.TOKEN_GATE:
				await handlePlaceTokenGateOrder();
				break;
			default:
				break;
		}
	}
}

function handlePaymentFail() {
	isOpenTicketPaymentFormSideBar.value = false;
	checkoutProcessStatusTemplate.value = CHECKOUT_PROCESS_STATUS_TEMPLATE.PAYMENT_FAILED;
	isOpenCheckoutProcessStatusSideBar.value = true;

	trackEvent({
		event: GTM_EVENT_NAME.PAYMENT_FAILURE,
		user_id: profileStore.profileId,
		event_id: eventId.value,
		order_amount: orderTotalAmount.value,
		ticket_id: orderTicketIds.value,
		payment_method: paymentMethod.value,
		questionnaire_id: orderFormId.value,
		discount_code_id: orderPromoCodeId.value,
		discount_code: orderPromoCode.value,
	});
}

function handlePaymentSuccess() {
	isOpenTicketPaymentFormSideBar.value = false;
	checkoutProcessStatusTemplate.value = CHECKOUT_PROCESS_STATUS_TEMPLATE.PAYMENT_SUCCESS;
	isOpenCheckoutProcessStatusSideBar.value = true;

	trackEvent({
		event: GTM_EVENT_NAME.PAYMENT_SUCCESS,
		user_id: profileStore.profileId,
		sales_id: orderSalesId.value,
		event_id: eventId.value,
		order_amount: orderTotalAmount.value,
		ticket_id: orderTicketIds.value,
		payment_method: paymentMethod.value,
		questionnaire_id: orderFormId.value,
		discount_code_id: orderPromoCodeId.value,
		discount_code: orderPromoCode.value,
	});
}

function handleTokenGateFail() {
	isOpenTokenGateProcessingSidebar.value = false;
	checkoutProcessStatusTemplate.value = CHECKOUT_PROCESS_STATUS_TEMPLATE.PAYMENT_FAILED;
	isOpenCheckoutProcessStatusSideBar.value = true;

	trackEvent({
		event: GTM_EVENT_NAME.CHECK_OUT_WITH_TOKEN_GATE_FAILURE,
		user_id: profileStore.profileId,
		event_id: eventId.value,
		token_id: routeTokenId.value, // Get an ID from redirect query param
	});
}

function handleTokenGateSuccess() {
	isOpenTokenGateProcessingSidebar.value = false;
	checkoutProcessStatusTemplate.value = CHECKOUT_PROCESS_STATUS_TEMPLATE.PAYMENT_SUCCESS;
	isOpenCheckoutProcessStatusSideBar.value = true;

	trackEvent({
		event: GTM_EVENT_NAME.CHECK_OUT_WITH_TOKEN_GATE_SUCCESS,
		user_id: profileStore.profileId,
		event_id: eventId.value,
		sales_id: orderSalesId.value,
		ticket_id: orderTicketIds.value,
		token_id: routeTokenId.value, // Get an ID from redirect query param
		questionnaire_id: orderFormId.value,
	});
}

function handleOrderStatusFail() {
	isOpenTicketPaymentFormSideBar.value = false;
	checkoutProcessStatusTemplate.value =
    CHECKOUT_PROCESS_STATUS_TEMPLATE.PAYMENT_SOMETHING_WENT_WRONG;
	isOpenCheckoutProcessStatusSideBar.value = true;

	trackEvent({
		event: GTM_EVENT_NAME.PAYMENT_FAILURE,
		user_id: profileStore.profileId,
		event_id: eventId.value,
		order_amount: orderTotalAmount.value,
		ticket_id: orderTicketIds.value,
		payment_method: paymentMethod.value,
		questionnaire_id: orderFormId.value,
		discount_code_id: orderPromoCodeId.value,
		discount_code: orderPromoCode.value,
	});
}

function handleOnOpenSendMessageDialog() {
	showContactDialog.value = false;
	if (!authStore.isLoggedIn) {
		isOpenAuthenticationSidebar.value = true;
		afterAuthenticationStep.value = AFTER_AUTHENTICATION_STEP.MESSAGE;
		return;
	}
	showSendMessageDialog.value = true;
}

async function handleOnSendMessage(subject: string, body: string) {
	if (!eventId.value) {
		return;
	}

	await eventStore.sendMessageToOrganizer(eventId.value, {
		body,
		subject,
	});

	if (eventStore.message.errorMessage) {
		notificationStore.showErrorNotification(eventStore.message.errorMessage);
	} else {
		notificationStore.showSuccessNotification('Successfully sent the message.');
		trackEvent({
			event: GTM_EVENT_NAME.MESSAGE_SENT,
			user_id: profileStore.profileId,
			group_id: organizerGroupId.value,
			message_sending_method: GTM_MESSAGE_SENDING_METHOD_PARAMS.INDIVIDUAL,
		});
	}

	// TODO: Integrate send a message to an organizer API later
	showSendMessageDialog.value = false;
}

async function updateFollowingStatus() {
	isOpenAuthenticationSidebar.value = false;
	if (organizerGroupId.value && profileId.value) {
		// Update following status
		await userStore.getGroupRole(profileId.value, organizerGroupId.value);
	}
}

function handleOnSignIn() {
	isOpenAuthenticationSidebar.value = true;
	afterAuthenticationStep.value = AFTER_AUTHENTICATION_STEP.FOLLOW;
}

async function handleOnSuccessAuthentication(isLoggedIn: boolean) {
	if (afterAuthenticationStep.value === AFTER_AUTHENTICATION_STEP.CHECKOUT) {
		handleContinueCheckoutFromLogin(isLoggedIn);
	} else if (afterAuthenticationStep.value === AFTER_AUTHENTICATION_STEP.FOLLOW) {
		isOpenAuthenticationSidebar.value = false;
		await followBtnRef.value.handleFollowGroup();
	} else if (afterAuthenticationStep.value === AFTER_AUTHENTICATION_STEP.MESSAGE) {
		isOpenAuthenticationSidebar.value = false;
		showSendMessageDialog.value = true;
	}
	afterAuthenticationStep.value = null;
}

function handleCopyShareLink() {
	if (props.isPreviewMode) {
		return;
	}

	const urlPath = getFullUrlPath(route.fullPath);

	copyToClipboard(urlPath)
		.then(() => {
			notificationStore.showSuccessNotification('Successfully copied URL to clipboard.');

			trackEvent({
				event: GTM_EVENT_NAME.SHARE_EVENT_SUCCESS,
				user_id: profileStore.profileId,
				group_id: organizerGroupId.value,
				event_id: eventId.value,
			});
		})
		.catch(() => {
			notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.TRY_AGAIN);
		});
}

function handleOpenContactDialog() {
	if (props.isPreviewMode) {
		return;
	}
	showContactDialog.value = true;
}

function handleOpenTicketDetail() {
	if (!eventId.value) {
		return;
	}
	isOpenCheckoutProcessStatusSideBar.value = false;
	router.push({ name: ROUTE.PROFILE_ATTENDING.NAME, query: { eventId: eventId.value } });
}

function handleSelectPaymentMethod(method: string) {
	paymentMethod.value = method;
}

function handleGoogleLoginFailed() {
	isShowGoogleLoginFailDialog.value = true;
}

async function handleTranslateToggle() {
	if (props.isPreviewMode || !props.event) {
		return;
	}

	if (isTranslatedView.value) {
		setTranslatedView(false);
		return;
	}

	const { title, groupName } = await translate({
		title: originalText.title,
		groupName: originalText.groupName,
	});

	// Preserve line breaks in description
	const descLines = originalText.description.split('\n');

	// Create a map of non-empty lines to translate
	const linesToTranslate: Record<string, string> = {};
	const lineIndices: number[] = [];

	descLines.forEach((line, index) => {
		if (line.trim() !== '') {
			// Use index as key to maintain order
			const key = `line_${index}`;
			linesToTranslate[key] = line;
			lineIndices.push(index);
		}
	});

	// Translate all non-empty lines in a single API call
	let translatedLines: Record<string, string> = {};
	if (Object.keys(linesToTranslate).length > 0) {
		translatedLines = await translate(linesToTranslate);
	}

	// Rebuild the translated description with original line breaks
	const translatedDescLines = descLines.map((line, index) => {
		if (line.trim() === '') return '';
		const key = `line_${index}`;
		return translatedLines[key] || line;
	});
	translatedText.title = title;
	translatedText.description = translatedDescLines.join('\n');
	translatedText.groupName = groupName;

	// Translate tickets only when user clicks the button
	if (originalText.tickets && originalText.tickets.length) {
		const nameMap: Record<string, string> = {};
		originalText.tickets.forEach((ticket) => {
			nameMap[String(ticket.id)] = ticket.name;
		});
		const translated = await translate(nameMap);
		translatedText.tickets = originalText.tickets.map((ticket) => ({
			...ticket,
			name: translated[String(ticket.id)] || ticket.name,
		}));
	} else {
		translatedText.tickets = [];
	}

	// Fire GTM translation success event only if API was used
	trackTranslationSuccess(profileStore.profileId);
	setTranslatedView(true);
}

async function setTokenGatedItem() {
	if (!eventId.value) {
		return;
	}

	await Promise.all([
		checkoutStore.getTicketsInventory(eventId.value),
		checkoutStore.getCheckoutOrderDetailByOrderId(routeOrderId.value),
	]);
	const data = checkoutStore.orderTickets
		.filter((ticket) => ticket.requiresTokenGates)
		.map((ticket): CartOrderForm => {
			return {
				id: ticket.id,
				name: ticket.name,
				seatAvailable: ticket.seatsAvailable,
				price: ticket.price,
				discountPrice: ticket.discountPrice,
				quantity: 1,
				status: ticket.status,
				currency: ticket.currency,
			};
		});
	Object.assign(checkoutStore.cart.orders, data);
}

watch(
	() => props.event?.organizerId,
	(organizerId) => {
		if (!organizerId) {
			return;
		}
		getUserInfo(organizerId);
	},
);

watch(
	() => props.event?.organizerId,
	(organizerId) => {
		if (!organizerId) {
			return;
		}
		getUserInfo(organizerId);
	},
);

watch(
	() => props.event?.id,
	(newEventId) => {
		if (!newEventId || !authStore.isLoggedIn) {
			return;
		}
		userStore.checkTicketOwnership(newEventId);
	},
);

watch(
	() => [profileStore.profile?.id, props.event?.organizerGroupId],
	([newProfileId, newOrganizerGroupId]) => {
		if (!newProfileId || !newOrganizerGroupId) {
			return;
		}
		userStore.getGroupRole(newProfileId, newOrganizerGroupId);
	},
);

watch(
	() => isOpenCheckoutProcessStatusSideBar.value,
	(newValue) => {
		// Retrieve the latest ticket status following payment completion
		if (newValue && eventId.value) {
			eventStore.retrieveEventDetail(eventId.value);
		}
	},
);

watch(
	[() => props.event, () => groupStore.groupInfo],
	([event, groupInfo]) => {
		if (event) {
			originalText.title = event.title || '';
			originalText.description = event.description || '';
			originalText.tickets = event.tickets || [];
		}
		originalText.groupName = groupInfo?.name || '';
		// Reset translation state on event/group change
		setTranslatedView(false);
		translatedText.title = '';
		translatedText.description = '';
		translatedText.groupName = '';
		translatedText.tickets = [];
	},
	{ immediate: true },
);

onMounted(async () => {
	if (route.query) {
		routeOrderId.value = String(route.query.orderId || '');
		routeTokenId.value = String(route.query.tokenId || '');
		isPaidToken.value = String(route.query.isPaid) === '1';

		// Remove query string
		router.push({ query: {} });
	}

	if (props.event?.organizerId) {
		getUserInfo(props.event.organizerId);
	}

	// Handle when redirect back from primerose's token gate
	if (routeOrderId.value) {
		await checkoutStore.getCheckoutOrdersStatusById(routeOrderId.value);
		if (orderStatus.value === CHECKOUT_ORDER_STATUS.REQUIRES_TOKEN) {
			notificationStore.showErrorNotification('Your token gated item is failed to process.', 'Please try to varify it again.');
			return;
		}

		if (isPaidToken.value) {
			// Set token gated item
			await setTokenGatedItem();
			// Get payment methods
			await checkoutStore.getCheckoutOrderPaymentMethodsByOrderId(routeOrderId.value);
			isOpenTicketPaymentFormSideBar.value = true;
		} else {
			// Update buyer info
			const payload = generateBuyerInfo(profileStore.profile, true, false);
			await checkoutStore.updateCheckoutOrderBuyerInfoByOrderId(routeOrderId.value, payload);
			if (checkoutStore.updateCheckoutOrderResponse.errorMessage) {
				notificationStore.showErrorNotification(checkoutStore.updateCheckoutOrderResponse.errorMessage);
				return;
			}

			// Confirm order
			checkoutStore.confirmCheckoutOrdersByOrderId({
				orderId: routeOrderId.value,
			});
			// Open polling order status sidebar
			isOpenTokenGateProcessingSidebar.value = true;
		}
	}
});
</script>

<template>
  <Head>
    <template v-if="isUSEvent">
      <link
        rel="canonical"
        :href="getFullUrlPath(`/event/${eventId}`)"
        hreflang="en-US"
      >
    </template>
    <template v-else>
      <link
        rel="canonical"
        :href="`https://peatix.com/event/${eventId}`"
      >
      <link
        rel="alternate"
        :href="getFullUrlPath(`/event/${eventId}`)"
        hreflang="en-US"
      >
    </template>
  </Head>
  <VirtualLinkAlert
    v-if="event?.status === EVENT_STATUS.OPEN && isAttend"
    class="event-alert"
    :link="event?.url"
    :status="event?.status"
    :is-happening-now="eventStore.isHappeningNow"
    is-event-page
  >
    <div class="content-container">
      <AnnouncementIcon class="announcement-icon" />
      <div class="information-container">
        <p class="text-sm text-semibold">
          You’re attending this event!
        </p>
        <p class="text-sm text-regular">
          {{ event?.instruction }}
        </p>
      </div>
    </div>
  </VirtualLinkAlert>
  <div class="event-cover text-md text-regular">
    <div :class="['image', { 'place-holder': !event?.coverUrl }]">
      <ImageSkeletonLoader
        v-if="isLoading"
        height="100%"
        width="100%"
      />
      <img
        v-else
        :src="coverImageUrl"
      >
    </div>
  </div>

  <div class="translation-container">
    <BaseButton
      class="translation-button"
      variant="outlined"
      color="gray"
      size="md"
      type="button"
      :disabled="isTranslationDisabled"
      @click="handleTranslateToggle"
    >
      <TranslateIcon :color="isTranslationDisabled ? ICON_COLOR.DISABLED : ICON_COLOR.DEFAULT" />
      {{ translationButtonText }}
    </BaseButton>
  </div>

  <div class="event-title-container">
    <div class="event-title-wrapper">
      <div class="title-wrapper">
        <template v-if="isLoading">
          <VSkeletonLoader
            class="title-loader"
            type="list-item"
            width="100%"
            height="24"
          />
          <ButtonSkeletonLoader
            v-if="isMobile"
            class="calendar-loader"
            width="77"
            height="78"
          />
        </template>
        <template v-else>
          <div
            v-if="isMobile"
            class="title-wrapper-mobile"
          >
            <h1 :class="['text-semibold', isMobile ? 'text-display-xs' : 'text-display-md']">
              {{ displayText.title }}
            </h1>
            <CalendarItem
              v-if="event?.startDateTime"
              :date="event?.startDateTime"
              :timezone="event?.timezone"
            />
          </div>
          <h1
            v-else
            :class="['text-semibold', isMobile ? 'text-display-xs' : 'text-display-md']"
          >
            {{ displayText.title }}
          </h1>
        </template>
      </div>
      <div class="sub-title-wrapper">
        <div
          v-if="isLoading"
          class="sub-title-loader"
        >
          <VSkeletonLoader
            class="avatar-loader"
            type="list-item-avatar"
          />
          <VSkeletonLoader type="list-item" />
        </div>
        <template v-else>
          <AvatarLink
            :image-url="groupStore.groupInfo?.logo"
            :size="AvatarSizes[0]"
            :user-id="userStore.user?.id"
            :group-id="eventStore.eventDetail?.organizerGroupId"
            :is-preview-mode="isPreviewMode"
          >
            <template #prefix>
              <p class="text-sm no-wrap">
                Hosted by
              </p>
            </template>
            <template #content>
              <p>{{ displayText.groupName }}</p>
            </template>
          </AvatarLink>
          <BaseButton
            class="share-btn"
            variant="link"
            color="primary"
            size="md"
            @click="handleCopyShareLink"
          >
            <ShareIcon />
            <span class="no-wrap text-semibold">
              {{ isMobile ? 'Share' : 'Share this event' }}
            </span>
          </BaseButton>
        </template>
      </div>
    </div>
    <div
      v-if="!isMobile"
      class="calendar-wrapper"
    >
      <ButtonSkeletonLoader
        v-if="isLoading"
        width="77"
        height="78"
      />
      <CalendarItem
        v-else-if="event?.startDateTime"
        :date="event?.startDateTime"
        :timezone="event?.timezone"
      />
    </div>
  </div>
  <div class="event-detail">
    <div class="left">
      <h2 class="text-sm text-medium">
        <FileIcon />
        About this event
      </h2>
      <template v-if="isLoading">
        <ParagraphSkeletonLoader />
        <ParagraphSkeletonLoader />
      </template>
      <template v-else>
        <SafeHtml
          v-if="displayText.description"
          class="text-sm text-regular"
          :html-content="displayText.description"
        />
      </template>
    </div>
    <div class="right">
      <BaseAlert v-if="isShowAlert">
        <p class="text-sm text-regular">
          <span>
            To purchase tickets for events outside the United States, please follow these
          </span>
          <a
            href="https://help-attendee.peatix.com/en/support/solutions/articles/44002540319?_gl=1*nzmkfj*_gcl_au*NjIzNjYzMzkyLjE3Mjk4MDAyNTc"
            target="_blank"
            rel="noopener noreferrer"
          >steps</a>
          <span>.</span>
        </p>
      </BaseAlert>
      <div
        v-if="tickets?.length || isLoading"
        class="item"
      >
        <h2 class="text-sm text-medium">
          <TicketIcon />
          Registration
        </h2>
        <template v-if="isLoading">
          <MultipleSkeletonLoader
            class="ticket-list-loader"
            :rows="3"
          >
            <div class="ticket-list-item-loader">
              <VSkeletonLoader
                type="list-item"
                width="100%"
              />
              <VSkeletonLoader
                type="list-item"
                width="100%"
              />
            </div>
          </MultipleSkeletonLoader>
          <ButtonSkeletonLoader class="get-ticket-loader" />
        </template>
        <template v-else>
          <TicketItems :tickets="isTranslatedView ? translatedText.tickets : originalText.tickets" />
          <template v-if="isShowAction">
            <BaseButton
              v-if="!hasOnlyTokenGatedTicketInEvent"
              :disabled="isDisabledPurchaseButton"
              @click="handleStartCheckout(TICKET_SELECTION_MODE.TICKETS)"
            >
              Get tickets
            </BaseButton>
            <BaseButton
              v-if="hasTokenGatedTicket && !hasOnlyTokenGatedTicketInEvent"
              variant="outlined"
              :disabled="isDisabledPurchaseButton"
              @click="handleStartCheckout(TICKET_SELECTION_MODE.TOKEN_GATE)"
            >
              <template #leftIcon>
                <CurrencyEthereumIcon color="#3538CD" />
              </template>
              Register as token holder
            </BaseButton>
            <BaseButton
              v-if="hasOnlyTokenGatedTicketInEvent"
              :disabled="isDisabledPurchaseButton"
              @click="handleStartCheckout(TICKET_SELECTION_MODE.TOKEN_GATE)"
            >
              <template #leftIcon>
                <CurrencyEthereumIcon color="#fff" />
              </template>
              Register as token holder
            </BaseButton>
          </template>
        </template>
      </div>
      <div class="item">
        <h2 class="text-sm text-medium">
          <CalendarIcon />
          Date & time
        </h2>
        <ParagraphSkeletonLoader v-if="isLoading" />
        <template v-else>
          <BaseChip v-if="eventStore.isHappeningNow && isPublished">
            <p>Happening now</p>
          </BaseChip>
          <template v-if="displayDateTime">
            <p class="text-sm text-regular pre-line">
              {{ displayDateTime }}
            </p>
            <BaseButton
              v-if="isShowAction"
              class="link"
              variant="link"
              size="sm"
              @click="showAddCalendarDialog = true"
            >
              Add to calendar
            </BaseButton>
            <button
              ref="btnAddGoogleCalendar"
              hidden
            />
          </template>
        </template>
      </div>
      <div class="item">
        <h2 class="text-sm text-medium">
          <MarkerPinIcon />
          Location
        </h2>
        <ParagraphSkeletonLoader v-if="isLoading" />
        <template v-else>
          <template v-if="event?.locationType === EVENT_TYPE.PHYSICAL && event?.location?.name">
            <p class="text-sm text-regular">
              {{ event?.location.name }}
            </p>
            <template v-if="event?.location.coordinate && isShowAction">
              <BaseGoogleMap
                v-if="showMap"
                :coordinate="event?.location.coordinate"
                height="191"
              />
              <BaseButton
                class="link"
                variant="link"
                size="sm"
                @click="showMap = !showMap"
              >
                {{ toggleMapText }}
                <template #rightIcon>
                  <component
                    :is="chevronIconComponent"
                    color="#444CE7"
                    width="16"
                    height="16"
                  />
                </template>
              </BaseButton>
            </template>
          </template>
          <p
            v-if="event?.locationType === EVENT_TYPE.ONLINE"
            class="text-sm text-regular"
          >
            Online event
          </p>
        </template>
      </div>
      <div class="item">
        <h2 class="text-sm text-medium">
          <UserIcon />
          Host
        </h2>
        <div class="event-host-container">
          <template v-if="isLoading">
            <VSkeletonLoader
              type="list-item-avatar"
              width="100%"
            />
            <div class="host-btn-container-loader">
              <ButtonSkeletonLoader width="100%" />
              <ButtonSkeletonLoader width="100%" />
            </div>
          </template>
          <template v-else>
            <AvatarLink
              :image-url="groupStore.groupInfo?.logo"
              :size="AvatarSizes[1]"
              :user-id="userStore.user?.id"
              :group-id="eventStore.eventDetail?.organizerGroupId"
              :is-preview-mode="isPreviewMode"
            >
              <template #content>
                <p>{{ displayText.groupName }}</p>
              </template>
            </AvatarLink>
            <div
              v-if="!isOwner"
              class="host-btn"
            >
              <FollowButton
                ref="followBtnRef"
                :user-id="profileId"
                :group-id="organizerGroupId"
                :role="userRole"
                :is-loading="userStore.isLoading"
                :is-logged-in="authStore.isLoggedIn"
                :disabled="isPreviewMode"
                is-event-page
                @on-update="updateFollowingStatus"
                @on-sign-in="handleOnSignIn"
              />
              <BaseButton
                variant="outlined"
                color="gray"
                size="sm"
                @click="handleOpenContactDialog"
              >
                Contact
              </BaseButton>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <ContactDialog
    :is-show="showContactDialog"
    @on-close="showContactDialog = false"
    @on-open-dialog="handleOnOpenSendMessageDialog"
  />
  <SendMessageDialog
    :receiver="organizerName"
    :is-show="showSendMessageDialog"
    :disabled="eventStore.isLoading"
    @on-close="showSendMessageDialog = false"
    @on-send="handleOnSendMessage"
  />
  <AddEventToCalendarDialog
    :is-show="showAddCalendarDialog"
    @on-add-google-calendar="handleAddToGoogleCalendar(EVENT_CALENDAR_TYPE.GOOGLE)"
    @on-download-calendar="handleAddToGoogleCalendar(EVENT_CALENDAR_TYPE.ICAL)"
    @on-close="showAddCalendarDialog = false"
  />
  <TicketSelectionSideDrawer
    :is-open="isOpenCheckoutSidebar"
    :event-id="eventId"
    :is-creating-order="checkoutStore.checkoutOrderNewResponse.isLoading"
    :selection-mode="ticketSelectionMode"
    :tickets="sidebarTickets"
    :is-translated="isTranslatedView"
    @on-close="handleSidebarClose"
    @on-continue-checkout="handleCartAdded"
  />
  <AuthenticationSideDrawerForm
    :is-open="isOpenAuthenticationSidebar"
    :is-loading="authStore.isLoading || checkoutStore.checkoutOrderNewResponse.isLoading"
    :redirect-ids="{ eventId }"
    :page="AUTH_PAGE_ORIGIN.EVENT"
    @on-close="handleSidebarClose"
    @on-cancel="handleSidebarClose"
    @on-success="handleOnSuccessAuthentication"
    @on-google-login-failed="handleGoogleLoginFailed"
  />
  <QuestionnaireSurveyFormSideDrawer
    :is-open="isOpenQuestionnaireFormSideBar"
    :form-id="checkoutStore.surveyFormId"
    :event-id="eventId"
    @on-close="handleSidebarClose"
    @on-back="handleBackFromQuestionnaire"
    @on-submitted="handleSubmittedSurveyForm"
  />
  <TicketPaymentFormSideDrawer
    :is-open="isOpenTicketPaymentFormSideBar"
    :event-id="eventId"
    :order-id="routeOrderId"
    :event-name="displayText.title"
    :event-country="eventCountryId"
    :is-paid-token="isPaidToken"
    @on-place-order-payment="handleSelectPaymentMethod"
    @on-back="handleBackFromPayment"
    @on-payment-fail="handlePaymentFail"
    @on-payment-success="handlePaymentSuccess"
    @on-order-status-fail="handleOrderStatusFail"
    @on-close="handleSidebarClose"
  />
  <TokenGateProcessingSideDrawer
    :event-id="eventId"
    :order-id="routeOrderId"
    :is-open="isOpenTokenGateProcessingSidebar"
    @on-token-gate-success="handleTokenGateSuccess"
    @on-token-gate-fail="handleTokenGateFail"
  />
  <CheckoutProcessStatusSideDrawer
    :is-open="isOpenCheckoutProcessStatusSideBar"
    :status="checkoutProcessStatusTemplate"
    @on-close="handleSidebarClose"
    @on-add-calendar="showAddCalendarDialog = true"
    @on-open-ticket-detail="handleOpenTicketDetail"
  />
  <GoogleLoginFailByInAppBrowserDialog
    :is-show="isShowGoogleLoginFailDialog"
    @on-close="isShowGoogleLoginFailDialog = false"
    @on-confirm="isShowGoogleLoginFailDialog = false"
  />
</template>

<style scoped lang="scss">
.event-alert {
  margin-bottom: spacings-get(8);

  .announcement-icon {
    min-width: spacings-get(5);
    min-height: spacings-get(5);
  }

  @include media-query-max(mobile) {
    margin: 0 spacings-get(4) spacings-get(8);
  }

  .content-container {
    display: flex;
    column-gap: spacings-get(3);

    .information-container {
      display: flex;
      flex-direction: column;
      row-gap: spacings-get(1);
    }
  }
}

.event-cover {
  margin-bottom: spacings-get(8);

  .image {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    overflow: hidden;
    color: colors-get(gray, 500);
    background-color: colors-get(gray, 200);
    @include border-radius-default;

    &.place-holder {
      width: 100%;
      aspect-ratio: 2/1;

      img {
        width: auto;
        height: auto;
      }

      @include media-query-max(mobile) {
        height: rem(188);
      }
    }

    img {
      width: 100%;
      height: 100%;
    }

    @include media-query-max(mobile) {
      text-align: center;
      height: rem(188);
      padding: 0;
      @include border-radius-none;
    }

    .action {
      position: absolute;
      bottom: 0;
      left: 0;
      margin: spacings-get(5);
      display: flex;
      gap: spacings-get(3);

      .icon-btn {
        padding: spacings-get(3);
      }
    }
  }
}

.translation-container {
  position: sticky;
  top: rem(74);
  z-index: 2;
  margin-bottom: spacings-get(8);

  @include media-query-max(mobile) {
    padding-left: spacings-get(4);
  }

  .translation-button {
    padding: spacings-get(2) rem(14);
  }
}

.event-title-container {
  display: grid;
  grid-template-columns: 1fr rem(77);
  justify-content: space-between;
  gap: spacings-get(4);
  margin-bottom: spacings-get(6);
  color: colors-get(gray, 950);

  @include media-query-max(mobile) {
    grid-template-columns: 1fr;
  }

  h1 {
    color: colors-get(gray, 950);
  }

  .event-title-wrapper {
    display: flex;
    flex-direction: column;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: spacings-get(4);

    .title-wrapper-mobile {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: spacings-get(4);
    }

    .calendar-loader {
      min-width: rem(77);
    }
  }

  .sub-title-wrapper {
    display: flex;
    align-items: center;
    gap: spacings-get(2);
    margin-top: spacings-get(2);

    .share-btn {
      padding: 0;
    }

    @include media-query-max(mobile) {
      column-gap: spacings-get(4);
    }
  }
}

.event-detail {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: spacings-get(8);
  margin: spacings-get(3) 0;
  padding-bottom: spacings-get(8);
  color: colors-get(gray, 950);

  h2 {
    display: flex;
    gap: spacings-get(2);
    color: colors-get(gray, 900);
  }

  > .right {
    display: flex;
    flex-direction: column;
    gap: spacings-get(6);

    .item {
      display: flex;
      flex-direction: column;
      row-gap: spacings-get(2);
      padding-bottom: spacings-get(6);
      color: colors-get(gray, 700);

      &:not(:last-child) {
        border-bottom: 1px solid colors-get(gray, 300);
      }

      .link {
        width: fit-content;
        justify-content: start;
        gap: rem(2);
        padding-left: 0;
      }
    }

    .event-host-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      gap: spacings-get(4);

      .host-btn {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: spacings-get(2);
      }

      .host-btn-container-loader {
        width: 100%;
        display: flex;
        gap: spacings-get(2);
      }

      :deep(.follow-btn) {
        width: 100%;
      }
    }
  }

  > .left {
    display: flex;
    flex-direction: column;
    gap: spacings-get(3);

    :deep(li) {
      margin-left: spacings-get(4);
    }
  }

  @include media-query-max(mobile) {
    display: flex;
    flex-direction: column;

    > .left {
      padding-bottom: spacings-get(8);
      border-bottom: 1px solid colors-get(gray, 300);
    }
  }
}

@include media-query-max(mobile) {
  .event-title-container,
  .event-detail {
    padding: 0 spacings-get(4);
  }
}

.title-loader {
  :deep(.v-skeleton-loader__text) {
    height: rem(24);
    border-radius: rem(34);
  }
}

.sub-title-loader {
  display: grid;
  grid-template-columns: rem(200) rem(100);
  width: 50%;

  @include media-query-max(mobile) {
    grid-template-columns: 70% 30%;
    width: 100%;
  }

  .avatar-loader {
    :deep(.v-skeleton-loader__avatar) {
      min-width: unset;
      min-height: unset;
      width: rem(24);
      height: rem(24);
    }
  }
}

.ticket-list-loader {
  margin: spacings-get(3) 0;

  .ticket-list-item-loader {
    display: grid;
    justify-content: space-between;
    grid-template-columns: rem(100) rem(70);
  }
}

.get-ticket-loader {
  margin-top: spacings-get(4);
}
</style>
